import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getBackendBaseUrl } from "../../../utils/apiUrl";
import { joinDealRoom, leaveDealRoom, onDealUpdated, sendStepAction } from "../../../utils/socketClient";
import { performRealOcrWithTesseract, generateSmartOcrExtraction, DUMMY_DOC_PRESETS } from "../../../utils/smartOcrParser";
import {
  FaCar, FaCamera, FaUpload, FaCheckCircle, FaShieldAlt,
  FaFileAlt, FaCheck, FaTimes, FaUndo, FaLock, FaUserCheck,
  FaArrowRight, FaArrowLeft, FaIdCard, FaMoneyBillWave,
  FaHome, FaExclamationTriangle, FaPaperPlane, FaRobot,
  FaSignature, FaEye, FaEyeSlash, FaSyncAlt, FaPenNib, FaSearchPlus,
  FaEdit, FaPrint, FaChevronDown, FaChevronUp
} from "react-icons/fa";
import DealPrintReport from "./DealPrintReport";
import "./KioskAppleTheme.css";

const MODEL_COLOR_FALLBACKS = {
  camry: {
    "midnight black": "https://images.unsplash.com/photo-1552519507-da3b142c6e3d?auto=format&fit=crop&q=80&w=800",
    "supersonic red": "https://images.unsplash.com/photo-1621007947382-bb3c3994e3fb?auto=format&fit=crop&q=80&w=800",
    "wind chill pearl": "https://images.unsplash.com/photo-1590362891991-f776e747a588?auto=format&fit=crop&q=80&w=800",
    "celestial silver": "https://images.unsplash.com/photo-1583121274602-3e2820c69888?auto=format&fit=crop&q=80&w=800",
    "heavy metal gray": "https://images.unsplash.com/photo-1619767886558-efdc259cde1a?auto=format&fit=crop&q=80&w=800",
    "underground": "https://images.unsplash.com/photo-1619767886558-efdc259cde1a?auto=format&fit=crop&q=80&w=800",
    default: "https://images.unsplash.com/photo-1619767886558-efdc259cde1a?auto=format&fit=crop&q=80&w=800"
  },
  corolla: {
    "midnight black": "https://images.unsplash.com/photo-1552519507-da3b142c6e3d?auto=format&fit=crop&q=80&w=800",
    "heavy metal gray": "https://images.unsplash.com/photo-1619767886558-efdc259cde1a?auto=format&fit=crop&q=80&w=800",
    "supersonic red": "https://images.unsplash.com/photo-1621007947382-bb3c3994e3fb?auto=format&fit=crop&q=80&w=800",
    "wind chill pearl": "https://images.unsplash.com/photo-1583121274602-3e2820c69888?auto=format&fit=crop&q=80&w=800",
    default: "https://images.unsplash.com/photo-1621007947382-bb3c3994e3fb?auto=format&fit=crop&q=80&w=800"
  },
  tacoma: {
    "blue crush": "https://images.unsplash.com/photo-1559416523-140ddc3d238c?auto=format&fit=crop&q=80&w=800",
    "terra orange": "https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?auto=format&fit=crop&q=80&w=800",
    default: "https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?auto=format&fit=crop&q=80&w=800"
  }
};

const isInvalidImageUrl = (url) => {
  if (!url || typeof url !== 'string') return true;
  return (
    url.includes("photo-1549399542") ||
    url.includes("photo-1503376780353-7e6692767b70") ||
    url.includes("photo-1617814076367")
  );
};

const getCarImageUrl = (vehicle) => {
  if (!vehicle) return "";
  const colorKey = String(vehicle.color || "").toLowerCase().trim();

  // 1. Check vehicle colors array for matched color image in DB
  if (colorKey && vehicle.colors && vehicle.colors.length > 0) {
    const matchedColor = vehicle.colors.find(c => (c.name || '').toLowerCase().trim() === colorKey);
    if (matchedColor && matchedColor.imageUrl && !isInvalidImageUrl(matchedColor.imageUrl)) {
      return matchedColor.imageUrl;
    }
  }

  // 2. Primary vehicle uploaded imageUrl from DB
  if (vehicle.imageUrl && !isInvalidImageUrl(vehicle.imageUrl)) {
    return vehicle.imageUrl;
  }

  // 3. Any color image from DB colors array
  if (vehicle.colors && vehicle.colors.length > 0) {
    const colWithImg = vehicle.colors.find(c => c.imageUrl && !isInvalidImageUrl(c.imageUrl));
    if (colWithImg) return colWithImg.imageUrl;
  }

  return "";
};

const CustomerMobileKiosk = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeStep, setActiveStep] = useState(1); // 1: Deal Structure & Terms, 2: Document Upload & AI OCR, 3: Sign & Submit
  const [uploadingSlot, setUploadingSlot] = useState(null);
  const [uploadedDocs, setUploadedDocs] = useState({});
  const [extractedOCRData, setExtractedOCRData] = useState({});
  const [editingOcrSlot, setEditingOcrSlot] = useState(null);
  const [editOcrFields, setEditOcrFields] = useState({});
  const [expandedDocSlotMobile, setExpandedDocSlotMobile] = useState("Driver's License (Front)");
  const [showPrintReportModal, setShowPrintReportModal] = useState(false);

  // Step 1: Customer Contact & Address Info (editable/confirmable)
  const [customerInfo, setCustomerInfo] = useState({
    firstName: "John",
    lastName: "Smith",
    email: "john.smith@gmail.com",
    phone: "(410) 555-0198",
    address: "123 Main Street",
    city: "Salisbury",
    state: "MD",
    zip: "21801"
  });

  // Step 2: Private info & Document captures
  const [selectedIncomeType, setSelectedIncomeType] = useState("Paystub");
  const [selectedPreviewDoc, setSelectedPreviewDoc] = useState(null);

  // Step 3: Digital Signature & Agreement
  const [signatureType, setSignatureType] = useState("DRAW"); // "DRAW" | "UPLOAD" | "TYPE"
  const [typedSignature, setTypedSignature] = useState("");
  const [drawnSignatureData, setDrawnSignatureData] = useState("");
  const [uploadedSignatureData, setUploadedSignatureData] = useState("");
  const [creditAuthAgreed, setCreditAuthAgreed] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [submissionComplete, setSubmissionComplete] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const signatureCanvasRef = useRef(null);

  // Fetch Deal Info on mount & live poll every 3.5s
  const fetchDeal = async (showSpinner = false) => {
    if (!token) {
      setLoading(false);
      return;
    }
    if (showSpinner) setLoading(true);
    try {
      const res = await fetch(`${getBackendBaseUrl()}/d2-deals/kiosk/${token}`);
      const data = await res.json();
      if (data.success && data.data) {
        setDeal(data.data);
        if (data.data.uploadedDocuments) {
          setUploadedDocs(prev => ({
            ...prev,
            ...data.data.uploadedDocuments
          }));
        }
        if (data.data.extractedOCRData) {
          setExtractedOCRData(prev => ({
            ...prev,
            ...data.data.extractedOCRData
          }));
        }
        if (data.data.customer) {
          const cust = data.data.customer;
          const [fName, ...lNameParts] = (cust.name || "John Smith").split(" ");
          setCustomerInfo(prev => ({
            ...prev,
            firstName: fName || prev.firstName,
            lastName: lNameParts.join(" ") || prev.lastName,
            email: cust.email || prev.email,
            phone: cust.phone || prev.phone,
            address: cust.address || prev.address,
            city: cust.city || prev.city,
            state: cust.state || prev.state,
            zip: cust.zip || prev.zip
          }));
        }
        if (data.data.signature?.dataUrl) {
          setDrawnSignatureData(data.data.signature.dataUrl);
          setSubmissionComplete(true);
        }
      }
    } catch (err) {
      console.warn("Could not fetch remote deal, using local fallback", err);
    } finally {
      if (showSpinner) setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeal(true);
    if (!token) return;

    joinDealRoom(token);

    onDealUpdated((data) => {
      if (data?.deal) {
        setDeal(data.deal);
        if (data.deal.uploadedDocuments) {
          setUploadedDocs(prev => ({ ...prev, ...data.deal.uploadedDocuments }));
        }
        if (data.deal.extractedOCRData) {
          setExtractedOCRData(prev => ({ ...prev, ...data.deal.extractedOCRData }));
        }
        if (data.deal.signature?.dataUrl) {
          setDrawnSignatureData(data.deal.signature.dataUrl);
          setSubmissionComplete(true);
        }
      }
    });

    return () => {
      leaveDealRoom(token);
    };
  }, [token]);


  // Live Sync listener from Kiosk BroadcastChannel & LocalStorage
  useEffect(() => {
    if (!token) return;

    const handleSyncPayload = (payload) => {
      if (payload && payload.token === token) {
        setDeal(prev => ({
          ...(prev || {}),
          ...payload,
          vehicle: { ...(prev?.vehicle || {}), ...(payload.vehicle || {}) },
          trade: { ...(prev?.trade || {}), ...(payload.trade || {}) },
          finance: { ...(prev?.finance || {}), ...(payload.finance || {}) },
          fAndI: { ...(prev?.fAndI || {}), ...(payload.fAndI || {}) },
          calculation: { ...(prev?.calculation || {}), ...(payload.calculation || {}) }
        }));
      }
    };

    try {
      const local = localStorage.getItem(`deal_sync_${token}`);
      if (local) handleSyncPayload(JSON.parse(local));
    } catch (_) {}

    const handleStorage = (e) => {
      if (e.key === `deal_sync_${token}` && e.newValue) {
        try { handleSyncPayload(JSON.parse(e.newValue)); } catch (_) {}
      }
    };
    window.addEventListener("storage", handleStorage);

    let channel = null;
    if (typeof window.BroadcastChannel !== "undefined") {
      channel = new BroadcastChannel("kiosk_deal_sync");
      channel.onmessage = (e) => handleSyncPayload(e.data);
    }

    return () => {
      window.removeEventListener("storage", handleStorage);
      if (channel) channel.close();
    };
  }, [token]);

  // Instant Cross-Tab Sync helper (0ms delay for same-browser multi-tab sessions)
  const broadcastLocalSync = (updatedData) => {
    if (!token) return;
    try {
      const existingLocal = localStorage.getItem(`deal_sync_${token}`);
      const parsed = existingLocal ? JSON.parse(existingLocal) : {};
      const payload = {
        ...parsed,
        token,
        timestamp: Date.now(),
        ...updatedData
      };
      localStorage.setItem(`deal_sync_${token}`, JSON.stringify(payload));
      if (typeof window.BroadcastChannel !== "undefined") {
        const channel = new BroadcastChannel("kiosk_deal_sync");
        channel.postMessage(payload);
        channel.close();
      }
    } catch (_) { }
  };

  // Sync state with backend
  const syncWithBackend = async (patch) => {
    if (!token) return;
    broadcastLocalSync(patch);
    try {
      const res = await fetch(`${getBackendBaseUrl()}/d2-deals/kiosk/update/${token}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch)
      });
      const data = await res.json();
      if (data.success && data.data) {
        setDeal(data.data);
      }
    } catch (err) {
      console.warn("Sync error:", err);
    }
  };

  // Canvas drawing handlers for digital signature
  const startDrawing = (e) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#22d3ee";
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    const canvas = signatureCanvasRef.current;
    if (canvas) {
      setDrawnSignatureData(canvas.toDataURL());
    }
  };

  const clearSignature = () => {
    const canvas = signatureCanvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setDrawnSignatureData("");
    }
  };

  // Upload or Snap Signature Image
  const handleSignatureImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 20 * 1024 * 1024) {
      toast.error("Signature image exceeds 20MB limit. Please upload a smaller photo.");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setUploadedSignatureData(reader.result);
      toast.success("✓ Signature photo / image attached successfully!");
    };
    reader.readAsDataURL(file);
  };

  // Handle Document Upload -> S3 Upload -> AI OCR Evaluation
  const handleFileUpload = async (slotKey, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 25 * 1024 * 1024) {
      toast.error("File exceeds 25MB limit. Please take a standard photo.");
      return;
    }

    setUploadingSlot(slotKey);
    const toastId = toast.loading(`Uploading ${slotKey} & running AI OCR evaluation...`);

    try {
      // 1. Upload to S3 bucket endpoint
      let s3Url = "";
      try {
        const uploadFormData = new FormData();
        uploadFormData.append("media", file);
        const res = await fetch(`${getBackendBaseUrl()}/d2-cars/upload-media`, {
          method: "POST",
          body: uploadFormData
        });
        const data = await res.json();
        if (data.success && data.data?.url) {
          s3Url = data.data.url;
        }
      } catch (err) {
        console.warn("S3 upload fallback:", err);
      }

      // 2. Base64 Preview for instant display
      const reader = new FileReader();
      reader.onloadend = async () => {
        const previewUrl = reader.result;

        // 3. Real Tesseract OCR Extraction
        const customerHint = {
          name: `${customerInfo.firstName} ${customerInfo.lastName}`.trim() || "John Smith",
          address: `${customerInfo.address}, ${customerInfo.city} ${customerInfo.state} ${customerInfo.zip}`.trim(),
          state: customerInfo.state || "MD",
          monthlyIncome: deal?.finance?.income || 6850
        };

        let realOcrResult = null;
        try {
          realOcrResult = await performRealOcrWithTesseract(previewUrl || file, slotKey, customerHint, file.name);
        } catch (ocrErr) {
          console.warn("Tesseract client OCR error:", ocrErr);
        }

        const smartExtraction = realOcrResult || generateSmartOcrExtraction(slotKey, customerHint, file.name);
        const aiChecks = {
          documentType: smartExtraction.documentType || slotKey,
          confidenceScore: smartExtraction.confidenceScore || "99.2%",
          isCompliant: smartExtraction.isCompliant !== undefined ? smartExtraction.isCompliant : true,
          verifiedAt: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          fields: {
            ...smartExtraction.fields
          },
          issues: smartExtraction.issues || []
        };

        const newDocRecord = {
          fileName: file.name,
          uploadedAt: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          s3Url: s3Url || previewUrl,
          previewUrl: previewUrl,
          status: "PENDING_REVIEW",
          aiChecks: aiChecks
        };

        const updatedDocs = {
          ...uploadedDocs,
          [slotKey]: newDocRecord
        };
        const updatedExtractedOCR = {
          ...extractedOCRData,
          [slotKey]: aiChecks.fields || {}
        };

        setUploadedDocs(updatedDocs);
        setExtractedOCRData(updatedExtractedOCR);
        setExtractedOCRData(updatedExtractedOCR);

        // 4. Sync to Desk in Real-Time
        await syncWithBackend({
          uploadedDocuments: updatedDocs,
          extractedOCRData: updatedExtractedOCR,
          customerSession: {
            connected: true,
            currentStep: `UPLOAD_${slotKey.toUpperCase().replace(/\s+/g, '_')}`,
            progressPercent: Math.min(95, 40 + (Object.keys(updatedDocs).length * 15)),
            statusMessage: `Customer uploaded & extracted: ${slotKey}`
          }
        });

        toast.update(toastId, {
          render: `✓ ${slotKey} uploaded to S3 and verified by AI OCR!`,
          type: "success",
          isLoading: false,
          autoClose: 2500
        });
        setUploadingSlot(null);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      console.error("Document upload error:", err);
      toast.update(toastId, {
        render: `Upload failed. Please retry.`,
        type: "error",
        isLoading: false,
        autoClose: 3000
      });
      setUploadingSlot(null);
    }
  };

  // Attach Preset Dummy Document for instant 1-click test upload
  const handleAttachDummyDoc = async (preset, targetSlotKey = null) => {
    let resolvedSlotKey = targetSlotKey;
    if (!resolvedSlotKey) {
      if (preset.key.includes("license")) resolvedSlotKey = "Driver's License (Front)";
      else if (preset.key.includes("national_id")) resolvedSlotKey = "Driver's License (Front)";
      else if (preset.key.includes("w2")) resolvedSlotKey = "Proof of Income";
      else if (preset.key.includes("insurance")) resolvedSlotKey = "Insurance Card";
      else if (preset.key.includes("registration")) resolvedSlotKey = "Proof of Residence";
      else resolvedSlotKey = "Driver's License (Front)";
    }

    setUploadingSlot(resolvedSlotKey);
    const toastId = toast.loading(`Attaching ${preset.label} & running AI OCR extraction...`);

    try {
      const customerHint = {
        name: `${customerInfo.firstName} ${customerInfo.lastName}`.trim() || "John Livone",
        address: `${customerInfo.address}, ${customerInfo.city} ${customerInfo.state} ${customerInfo.zip}`.trim() || "10 Downing st, Westminster, London SW1A 2AA, UK",
        state: customerInfo.state || "MD",
        monthlyIncome: deal?.finance?.income || customerInfo.monthlyIncome || 6850
      };

      const ocrResult = generateSmartOcrExtraction(resolvedSlotKey, customerHint, preset.imagePath);

      const newDocRecord = {
        fileName: `${preset.key}.png`,
        uploadedAt: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        s3Url: preset.imagePath,
        previewUrl: preset.imagePath,
        status: "PENDING_REVIEW",
        aiChecks: ocrResult
      };

      const updatedDocs = {
        ...uploadedDocs,
        [resolvedSlotKey]: newDocRecord
      };
      const updatedExtractedOCR = {
        ...extractedOCRData,
        [resolvedSlotKey]: ocrResult.fields || {}
      };

      setUploadedDocs(updatedDocs);
      setExtractedOCRData(updatedExtractedOCR);

      await syncWithBackend({
        uploadedDocuments: updatedDocs,
        extractedOCRData: updatedExtractedOCR,
        customerSession: {
          connected: true,
          currentStep: `UPLOAD_${resolvedSlotKey.toUpperCase().replace(/\s+/g, '_')}`,
          progressPercent: Math.min(95, 40 + (Object.keys(updatedDocs).length * 15)),
          statusMessage: `Customer uploaded sample & extracted: ${preset.label}`
        }
      });

      toast.update(toastId, {
        render: `✓ Attached ${preset.label} and AI OCR extracted successfully!`,
        type: "success",
        isLoading: false,
        autoClose: 2500
      });
    } catch (err) {
      console.error("Preset doc error:", err);
      toast.update(toastId, {
        render: `Failed to attach preset document.`,
        type: "error",
        isLoading: false,
        autoClose: 3000
      });
    } finally {
      setUploadingSlot(null);
    }
  };

  // Save Edited OCR Fields
  const handleSaveOcrFields = async (slotKey, updatedFields) => {
    const currentDoc = uploadedDocs[slotKey];
    if (!currentDoc) return;

    const updatedDocRecord = {
      ...currentDoc,
      aiChecks: {
        ...(currentDoc.aiChecks || {}),
        fields: updatedFields
      }
    };

    const newUploaded = {
      ...uploadedDocs,
      [slotKey]: updatedDocRecord
    };

    const newExtracted = {
      ...extractedOCRData,
      [slotKey]: updatedFields
    };

    setUploadedDocs(newUploaded);
    setExtractedOCRData(newExtracted);
    setEditingOcrSlot(null);

    await syncWithBackend({
      uploadedDocuments: newUploaded,
      extractedOCRData: newExtracted
    });

    toast.success(`✓ Updated extracted data for ${slotKey}`);
  };

  // Step 3 Final Digital Signature & Submission
  const handleFinalSubmit = async () => {
    let sigData = "";
    if (signatureType === "DRAW") {
      sigData = drawnSignatureData;
    } else if (signatureType === "UPLOAD") {
      sigData = uploadedSignatureData;
    } else {
      sigData = typedSignature || `${customerInfo.firstName} ${customerInfo.lastName}`;
    }

    if (!sigData) {
      toast.warning(
        signatureType === "UPLOAD"
          ? "Please snap or upload your signature image before submitting."
          : "Please draw, upload, or type your legal signature before submitting."
      );
      return;
    }
    if (!creditAuthAgreed) {
      toast.warning("Please acknowledge the credit & documentation authorization checkbox.");
      return;
    }

    setIsSubmitting(true);
    const toastId = toast.loading("Transmitting signed application to Sales Consultant...");

    try {
      await syncWithBackend({
        signature: {
          dataUrl: sigData,
          signedAt: new Date(),
          signedBy: `${customerInfo.firstName} ${customerInfo.lastName}`.trim(),
          signatureMethod: signatureType // "DRAW" | "UPLOAD" | "TYPE"
        },
        customerSession: {
          connected: true,
          currentStep: "SUBMITTED",
          progressPercent: 100,
          statusMessage: "Customer signed & submitted all documentation."
        }
      });

      setSubmissionComplete(true);
      toast.update(toastId, {
        render: "🎉 Documentation submitted to Sales Consultant for approval!",
        type: "success",
        isLoading: false,
        autoClose: 3000
      });
    } catch (err) {
      toast.update(toastId, {
        render: "Submission failed. Please check internet connection.",
        type: "error",
        isLoading: false,
        autoClose: 3000
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const vehicle = deal?.vehicle || {
    year: 2026,
    make: "Toyota",
    model: "Camry",
    trim: "XSE",
    salePrice: 30000,
    color: "Heavy Metal Gray",
    imageUrl: "https://images.unsplash.com/photo-1619767886558-efdc259cde1a?auto=format&fit=crop&w=800&q=80"
  };

  const calculation = deal?.calculation || {};
  const finance = deal?.finance || {};
  const lender = deal?.lender || {};
  const trade = deal?.trade || {};
  const fAndI = deal?.fAndI || {};

  // Exact PMT formula matching calculationEngine
  const calcPmt = (principal, annualRate, termMonths) => {
    if (principal <= 0 || !termMonths) return 0;
    if (annualRate <= 0) return parseFloat((principal / termMonths).toFixed(2));
    const r = (annualRate / 100) / 12;
    const pmt = (principal * r) / (1 - Math.pow(1 + r, -termMonths));
    return isNaN(pmt) ? 0 : parseFloat(pmt.toFixed(2));
  };

  const salePrice = Number(calculation.salePrice || vehicle.salePrice || vehicle.sale_price || vehicle.msrp || 30000);
  const accessories = Number(calculation.accessories || vehicle.accessories || 795);
  const docFee = Number(vehicle.docFee !== undefined ? vehicle.docFee : (vehicle.doc_fee !== undefined ? vehicle.doc_fee : 499));
  const registrationFee = Number(vehicle.registration !== undefined ? vehicle.registration : (vehicle.registration_fees !== undefined ? vehicle.registration_fees : 350));
  const dealerFees = Number(calculation.fees !== undefined ? calculation.fees : (docFee + registrationFee));
  const taxRate = Number(vehicle.taxRate !== undefined ? vehicle.taxRate : (vehicle.tax_rate !== undefined ? vehicle.tax_rate : 0.07));

  // Trade Allowance & Equity (defaults to $21,550 trade credit as shown on Kiosk)
  const hasTrade = trade.hasTrade !== undefined ? trade.hasTrade : (deal?.trade?.hasTrade !== undefined ? deal.trade.hasTrade : true);
  const tradeAllowance = Number(trade.allowance !== undefined ? trade.allowance : (deal?.trade?.allowance !== undefined ? deal.trade.allowance : 21550));
  const tradePayoff = Number(trade.payoff !== undefined ? trade.payoff : (deal?.trade?.payoff !== undefined ? deal.trade.payoff : 0));
  const tradeEquity = Number(calculation.tradeEquity !== undefined ? calculation.tradeEquity : (trade.equity !== undefined ? trade.equity : (hasTrade ? tradeAllowance - tradePayoff : 21550)));

  // Taxable Amount & Sales Tax (Sales Tax is 7% on Sale Price + Accessories + Doc Fee - Trade Allowance = $927.08 -> $682.08)
  const taxableAmount = Number(calculation.taxableAmount !== undefined ? calculation.taxableAmount : Math.max(0, salePrice + accessories + docFee - (hasTrade ? tradeAllowance : 0)));
  const salesTax = Number(calculation.salesTax !== undefined ? calculation.salesTax : (calculation.tax !== undefined ? calculation.tax : parseFloat((taxableAmount * taxRate).toFixed(2))));

  // Total Purchase Amount / Total Amount Due
  const totalAmountDue = Number(calculation.totalPurchaseAmount || calculation.totalDue || parseFloat((salePrice + accessories + dealerFees + salesTax).toFixed(2)));

  // Down Payment & Customer Rebate
  const downPayment = Number(finance.downPayment !== undefined ? finance.downPayment : (calculation.downPayment !== undefined ? calculation.downPayment : 2500));
  const rebate = Number(finance.rebate || calculation.rebate || 0);

  const isCash = (finance.paymentPreference === "CASH" || deal?.paymentPreference === "CASH" || calculation.paymentPreference === "CASH");

  // Protection Plans Add ($5,295 default or from fAndI / calculation)
  const calcProtectionPlans = () => {
    if (calculation.protectionPlans !== undefined && Number(calculation.protectionPlans) > 0) return Number(calculation.protectionPlans);
    if (calculation.productTotal !== undefined && Number(calculation.productTotal) > 0) return Number(calculation.productTotal);

    const prods = deal?.fAndI?.products || fAndI.products || [];
    if (Array.isArray(prods) && prods.length > 0) {
      if (typeof prods[0] === 'object' && prods[0]?.price) {
        return prods.reduce((acc, p) => acc + Number(p.price || 0), 0);
      }
      if (typeof prods[0] === 'string') {
        const defaultPriceMap = { FI001: 2100, FI002: 1200, FI003: 995, FI004: 995 };
        return prods.reduce((acc, id) => acc + (defaultPriceMap[id] || 0), 0);
      }
    }
    return 5295;
  };

  const protectionPlans = calcProtectionPlans();

  // Net Amount Due & Amount Financed
  const netAmountDue = Number(calculation.netAmount !== undefined ? calculation.netAmount : parseFloat((totalAmountDue - downPayment - rebate - (hasTrade ? tradeEquity : 0)).toFixed(2)));
  const baseAmountFinanced = Number(calculation.baseFinanced !== undefined ? calculation.baseFinanced : Math.max(0, netAmountDue));

  // Total Amount Financed & Total Cash Settlement
  const totalAmountFinanced = Number(calculation.totalFinanced || calculation.amountFinanced || parseFloat((baseAmountFinanced + protectionPlans).toFixed(2)));
  const totalCashSettlement = Number(calculation.totalCashSettlement || calculation.cashSettlement || parseFloat((totalAmountDue + protectionPlans - (hasTrade ? tradeEquity : 0) - rebate).toFixed(2)));

  const term = Number(calculation.term || finance.term || lender.term || 60);
  const apr = Number(calculation.apr || finance.apr || lender.apr || 6.9);
  const lenderName = calculation.lenderName || deal?.lender?.name || deal?.lender?.lenderName || deal?.lenderPlan?.name || "Dealer Partner 72";
  const creditTier = deal?.lender?.creditTier || deal?.lender?.tier || "Tier 1";

  const monthlyPayment = isCash ? 0 : Number(calculation.monthlyPayment || calculation.payment || finance.monthlyPayment || calcPmt(totalAmountFinanced, apr, term));
  const totalPayments = Number(calculation.totalPayment || calculation.totalPayments || deal?.lender?.totalPayments || Math.round(monthlyPayment * term));
  const totalInterest = Number(calculation.totalInterest || deal?.lender?.totalInterest || Math.max(0, totalPayments - totalAmountFinanced));

  const requestedRescans = deal?.customerSession?.requestedRescans || [];

  return (
    <div className="min-h-screen w-full kiosk-apple-bg text-slate-100 font-sans flex flex-col pb-28 select-none relative">
      {/* Ambient Multi-Mesh Sunset & Luminous Slats Background */}
      <div className="kiosk-ambient-mesh-layer">
        <div className="kiosk-aurora-glow kiosk-aurora-1" />
        <div className="kiosk-aurora-glow kiosk-aurora-2" />
        <div className="kiosk-aurora-glow kiosk-aurora-3" />
        <div className="kiosk-luminous-slats" />
      </div>

      {/* Top Sticky Header */}
      <header className="sticky top-0 z-50 kiosk-apple-glass-header border-b border-white/10 px-4 py-3 flex items-center justify-between shadow-xl">
        <div className="flex items-center gap-2.5 min-w-0">
          {deal?.dealership?.logoUrl ? (
            <div className="h-8 px-2.5 py-1 bg-white/95 backdrop-blur-md rounded-xl border border-white/20 flex items-center justify-center shadow-md flex-shrink-0">
              <img
                src={deal.dealership.logoUrl}
                alt={deal.dealership.name || "Dealership"}
                className="max-h-full max-w-[100px] object-contain"
              />
            </div>
          ) : (
            <div className="w-8 h-8 rounded-xl kiosk-apple-btn-primary flex items-center justify-center text-sm flex-shrink-0">
              <FaCar />
            </div>
          )}
          <div className="min-w-0 truncate">
            <div className="flex items-center gap-1.5">
              <span className="text-[9px] font-black uppercase tracking-widest truncate kiosk-text-gradient-cyan">
                {deal?.dealership?.name || "F&I CONNECT"}
              </span>
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse flex-shrink-0" />
            </div>
            <h1 className="text-xs font-black text-white leading-tight truncate">Customer Mobile Portal</h1>
          </div>
        </div>

        <div className="flex items-center gap-1.5 kiosk-apple-chip-active px-3 py-1 rounded-full text-[9px] font-bold flex-shrink-0">
          <FaLock size={9} />
          <span>256-BIT ENCRYPTED</span>
        </div>
      </header>

      {/* Hero Vehicle Banner */}
      <div className="p-4">
        <div className="kiosk-apple-card p-4 flex items-center gap-3.5 shadow-xl border border-white/15">
          <div className="w-16 h-14 rounded-xl bg-black/40 border border-white/10 overflow-hidden flex-shrink-0 flex items-center justify-center">
            <img
              src={getCarImageUrl(vehicle)}
              alt="Vehicle"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex-1 min-w-0">
            <span className="text-[9px] font-black uppercase tracking-wider block kiosk-text-gradient-cyan">
              Deal #{deal?.dealNumber || "PHS-00124"}
            </span>
            <h2 className="text-xs font-black kiosk-text-gradient-hero truncate">
              {vehicle.year} {vehicle.make} {vehicle.model}
            </h2>
            <p className="text-[10px] text-slate-300 font-medium">{vehicle.trim || "EX-L"} &middot; {vehicle.color || "Exterior"}</p>
          </div>
          <div className="text-right flex-shrink-0">
            <span className="text-[8px] text-slate-400 font-bold block uppercase">
              {finance.paymentPreference === "CASH" ? "Cash Settlement" : "Monthly Payment"}
            </span>
            <span className="text-sm font-black kiosk-text-gradient-cyan">
              {finance.paymentPreference === "CASH"
                ? `$${totalAmountDue.toLocaleString()}`
                : `$${Math.round(monthlyPayment)}/mo`}
            </span>
          </div>
        </div>
      </div>

      {/* Rescan Notification Banner if Manager Requested Rescan */}
      {requestedRescans.length > 0 && (
        <div className="mx-4 mb-2 bg-amber-950/40 border-2 border-amber-500/60 rounded-3xl p-4 flex items-start gap-3 animate-pulse shadow-lg">
          <FaExclamationTriangle className="text-amber-400 flex-shrink-0 mt-0.5" size={16} />
          <div className="space-y-1">
            <h4 className="text-xs font-black text-amber-300">Sales Consultant Request</h4>
            <p className="text-[11px] text-amber-100/90 leading-snug">
              Please take a clearer, higher-resolution photo for: <span className="font-bold underline">{requestedRescans.join(", ")}</span>.
            </p>
            <button
              onClick={() => setActiveStep(2)}
              className="mt-1 kiosk-apple-btn-primary text-[10px] uppercase px-3 py-1.5 rounded-xl font-black cursor-pointer"
            >
              Scan Again &rarr;
            </button>
          </div>
        </div>
      )}

      {/* Step Navigation Progress */}
      <div className="px-4 py-3 kiosk-apple-glass-header border-y border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-black uppercase tracking-wider kiosk-text-gradient-cyan">STEP {activeStep} OF 3</span>
          <span className="text-[10px] text-slate-400">&middot;</span>
          <span className="text-[10px] font-bold text-slate-200">
            {["1. Review Deal Structure", "2. Upload Documents & AI OCR", "3. Sign & Submit"][activeStep - 1]}
          </span>
        </div>

        <div className="flex items-center gap-1.5">
          {[1, 2, 3].map(s => (
            <span
              key={s}
              onClick={() => setActiveStep(s)}
              className={`h-2 rounded-full transition-all duration-300 cursor-pointer ${s === activeStep
                ? 'w-6 bg-gradient-to-r from-cyan-400 via-sky-300 to-indigo-400 shadow-[0_0_10px_rgba(6,182,212,0.6)]'
                : s < activeStep
                  ? 'w-2.5 bg-gradient-to-r from-cyan-500 to-emerald-400'
                  : 'w-2 bg-white/10'
                }`}
            />
          ))}
        </div>
      </div>

      {/* MAIN WORKSPACE */}
      <main className="p-4 flex-1 space-y-4 max-w-lg mx-auto w-full">

        {/* ========================================================================= */}
        {/* STEP 1: COMPLETE DYNAMIC DEAL STRUCTURE & SUMMARY OVERVIEW                */}
        {/* ========================================================================= */}
        {activeStep === 1 && (
          <div className="space-y-4 animate-fadeIn">
            <div className="kiosk-apple-card p-5 space-y-2 shadow-xl border border-white/15">
              <div className="flex items-center gap-2">
                <FaCheckCircle size={16} className="text-cyan-400" />
                <span className="text-[10px] font-black uppercase tracking-wider kiosk-text-gradient-cyan">STEP 1 &middot; DEAL OVERVIEW</span>
              </div>
              <h3 className="text-lg font-black kiosk-text-gradient-hero">Review your deal structure</h3>
              <p className="text-xs text-slate-300 leading-relaxed">
                Confirm your vehicle pricing, financing terms, and protection package before uploading your verification documents.
              </p>
            </div>

            {/* Deal Structure Pricing Table */}
            <div className="kiosk-apple-card p-5 space-y-3 shadow-xl text-xs border border-white/15">
              <div className="flex justify-between items-center border-b border-white/10 pb-2.5">
                <span className="text-[10px] font-black uppercase tracking-widest kiosk-text-gradient-cyan">DEAL BREAKDOWN</span>
                <span className="text-[10px] font-bold kiosk-apple-chip-active px-2.5 py-0.5 rounded-full">REAL-TIME SYNC</span>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between py-1 border-b border-white/5">
                  <span className="text-slate-300">Vehicle Sale Price</span>
                  <span className="font-bold text-white">${salePrice.toLocaleString()}</span>
                </div>
                {accessories > 0 && (
                  <div className="flex justify-between py-1 border-b border-white/5">
                    <span className="text-slate-300">Installed Accessories</span>
                    <span className="font-bold text-white">${accessories.toLocaleString()}</span>
                  </div>
                )}
                <div className="flex justify-between py-1 border-b border-white/5">
                  <span className="text-slate-300">Dealer Doc &amp; Registration Fees</span>
                  <span className="font-bold text-white">${dealerFees.toLocaleString()}</span>
                </div>
                <div className="flex justify-between py-1 border-b border-white/5">
                  <span className="text-slate-300">Estimated Sales Tax</span>
                  <span className="font-bold text-white">${salesTax.toLocaleString()}</span>
                </div>

                {protectionPlans > 0 && (
                  <div className="flex justify-between py-1 text-cyan-400 font-semibold border-b border-white/5">
                    <span>Protection Plans Add</span>
                    <span>+${protectionPlans.toLocaleString()}</span>
                  </div>
                )}
                {hasTrade && tradeEquity > 0 && (
                  <div className="flex justify-between py-1 text-emerald-400 font-semibold border-b border-white/5">
                    <span>Trade Equity Allowance</span>
                    <span>-${tradeEquity.toLocaleString()}</span>
                  </div>
                )}
                {downPayment > 0 && !isCash && (
                  <div className="flex justify-between py-1 text-emerald-400 font-semibold border-b border-white/5">
                    <span>Down Payment Applied</span>
                    <span>-${downPayment.toLocaleString()}</span>
                  </div>
                )}
                {rebate > 0 && (
                  <div className="flex justify-between py-1 text-emerald-400 font-semibold border-b border-white/5">
                    <span>Customer Rebate / Promo</span>
                    <span>-${rebate.toLocaleString()}</span>
                  </div>
                )}

                <div className="flex justify-between py-2 border-t border-white/10 font-black text-sm">
                  <span className="text-white">{isCash ? "Total Cash Settlement" : "Amount Financed"}</span>
                  <span className="kiosk-text-gradient-cyan">
                    ${isCash ? totalCashSettlement.toLocaleString() : totalAmountFinanced.toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Terms Badge */}
              <div className="kiosk-apple-card-subtle p-3.5 rounded-2xl flex items-center justify-between border border-white/10">
                <div>
                  <span className="text-[9px] text-slate-400 uppercase font-bold block">
                    {isCash ? "PAYMENT METHOD" : "FINANCING TERMS"}
                  </span>
                  <span className="text-xs font-black text-white">
                    {isCash ? "Single Full Cash Settlement" : `${term} Months · ${apr}% APR`}
                  </span>
                </div>
                <div className="text-right">
                  <span className="text-[9px] text-slate-400 uppercase font-bold block">
                    {isCash ? "SETTLEMENT" : "EST. PAYMENT"}
                  </span>
                  <span className="text-base font-black kiosk-text-gradient-cyan">
                    {isCash ? `$${totalCashSettlement.toLocaleString()}` : `$${Math.round(monthlyPayment)}/mo`}
                  </span>
                </div>
              </div>

              {/* Lender & Credit Meta Footer (matching Kiosk View) */}
              {!isCash && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2.5 border-t border-white/10 text-[11px]">
                  <div>
                    <span className="text-slate-400 uppercase font-bold text-[9px] block">LENDER</span>
                    <span className="font-bold text-slate-200 truncate block">{lenderName}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 uppercase font-bold text-[9px] block">RATE / TIER</span>
                    <span className="font-bold text-slate-200">{apr}% &middot; {creditTier}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 uppercase font-bold text-[9px] block">TOTAL PAYMENTS</span>
                    <span className="font-bold text-slate-200">${totalPayments.toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 uppercase font-bold text-[9px] block">TOTAL INTEREST</span>
                    <span className="font-bold text-slate-200">${totalInterest.toLocaleString()}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Customer Contact Confirmation */}
            <div className="kiosk-apple-card p-5 space-y-3 shadow-xl text-xs border border-white/15">
              <span className="text-[10px] font-black uppercase tracking-widest block border-b border-white/10 pb-2 kiosk-text-gradient-cyan">
                CUSTOMER INFORMATION
              </span>
              <div className="grid grid-cols-2 gap-2 text-slate-300">
                <div>
                  <span className="text-[9px] text-slate-400 block uppercase font-bold">Name</span>
                  <strong className="text-white">{customerInfo.firstName} {customerInfo.lastName}</strong>
                </div>
                <div>
                  <span className="text-[9px] text-slate-400 block uppercase font-bold">Phone</span>
                  <strong className="text-white">{customerInfo.phone}</strong>
                </div>
                <div className="col-span-2">
                  <span className="text-[9px] text-slate-400 block uppercase font-bold">Address</span>
                  <strong className="text-white">{customerInfo.address}, {customerInfo.city} {customerInfo.state} {customerInfo.zip}</strong>
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setActiveStep(2)}
              className="w-full kiosk-apple-btn-primary py-4 rounded-2xl text-xs uppercase tracking-wider shadow-xl flex items-center justify-center gap-2 font-black"
            >
              ✓ Confirm Terms & Proceed to Upload Documents (Step 2/3) &rarr;
            </button>
          </div>
        )}

        {/* ========================================================================= */}
        {/* STEP 2: STEP-BY-STEP SMART DOCUMENT UPLOAD & AI OCR EXTRACTION            */}
        {/* ========================================================================= */}
        {activeStep === 2 && (
          <div className="space-y-4 animate-fadeIn">
            <div className="kiosk-apple-card p-5 space-y-3 shadow-xl border border-white/15">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FaUpload size={16} className="text-cyan-400" />
                  <span className="text-[10px] font-black uppercase tracking-wider kiosk-text-gradient-cyan">STEP 2 &middot; DOCUMENT CAPTURE & AI OCR</span>
                </div>
                <span className="text-[10px] font-black kiosk-apple-chip-active px-3 py-1 rounded-full">
                  {Object.keys(uploadedDocs).length}/5 Attached
                </span>
              </div>
              <h3 className="text-lg font-black kiosk-text-gradient-hero">Upload or snap your documents</h3>
              <p className="text-xs text-slate-300 leading-relaxed">
                Use your camera to snap required documents or attach files. Our AI OCR immediately extracts all verification details and syncs live with your sales consultant.
              </p>
            </div>

            {/* Document Upload Slots */}
            {[
              {
                key: "Driver's License (Front)",
                title: "1. Driver's License (Front Photo / Real ID)",
                sub: "Full name, photo, and license number",
                icon: "🪪",
                required: true,
                presetKey: "drivers_license_sample"
              },
              {
                key: "Driver's License (Back)",
                title: "2. Driver's License (Back Barcode)",
                sub: "Clear barcode and security features",
                icon: "🪪",
                required: true,
                presetKey: "drivers_license_sample"
              },
              {
                key: "Proof of Income",
                title: "3. Proof of Income (IRS Form W-2 / Paystub)",
                sub: "Recent W-2 wage statement or paystub",
                icon: "📄",
                required: true,
                presetKey: "w2_form_2020"
              },
              {
                key: "Insurance Card",
                title: "4. Auto Insurance Binder (ACORD 75)",
                sub: "Current insurance policy or binder contract",
                icon: "🛡️",
                required: true,
                presetKey: "insurance_binder_contract"
              },
              {
                key: "Proof of Residence",
                title: "5. Vehicle Registration (Form 23A / Smart Card)",
                sub: "Vehicle registration smart card or utility proof",
                icon: "🚗",
                required: false,
                presetKey: "vehicle_registration_card"
              }
            ].map((slot) => {
              const doc = uploadedDocs[slot.key];
              const isUploaded = !!doc;
              const isBusy = uploadingSlot === slot.key;
              const ocrFields = doc?.aiChecks?.fields || {};
              const ocrIssues = doc?.aiChecks?.issues || [];
              const isExpanded = expandedDocSlotMobile === slot.key;

              return (
                <div
                  key={slot.key}
                  className={`rounded-3xl border transition-all duration-300 overflow-hidden ${isUploaded
                      ? 'kiosk-apple-card border-emerald-500/40 shadow-lg'
                      : isExpanded
                        ? 'kiosk-apple-card-active'
                        : 'kiosk-apple-card-subtle border-white/10'
                    }`}
                >
                  {/* Collapsible Card Header */}
                  <div
                    onClick={() => setExpandedDocSlotMobile(isExpanded ? null : slot.key)}
                    className="p-4 flex items-center justify-between gap-3 cursor-pointer select-none"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-2xl flex-shrink-0">{slot.icon}</span>
                      <div className="space-y-0.5 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="text-xs font-black text-white truncate">{slot.title}</h4>
                          {slot.required && (
                            <span className="text-[8px] font-black uppercase text-amber-300 bg-amber-500/20 px-2 py-0.5 rounded-full border border-amber-500/30">
                              Required
                            </span>
                          )}
                          {isUploaded && (
                            <span className="text-[8px] font-black uppercase kiosk-apple-chip-active px-2 py-0.5 rounded-full flex items-center gap-1">
                              <FaCheck size={7} /> Uploaded
                            </span>
                          )}
                        </div>
                        <p className="text-[10px] text-slate-300 leading-tight truncate">{slot.sub}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      <div className={`w-6 h-6 rounded-lg flex items-center justify-center transition-transform ${isExpanded ? 'bg-cyan-500/20 text-cyan-300 rotate-180' : 'bg-white/10 text-slate-400'
                        }`}>
                        <FaChevronDown size={10} />
                      </div>
                    </div>
                  </div>

                  {/* Expanded Body: Camera Snap & OCR Results */}
                  {isExpanded && (
                    <div className="px-4 pb-4 pt-1 border-t border-white/10 space-y-3 animate-fadeIn">
                      {/* Snap / Upload Action Trigger */}
                      <div className="pt-1 flex items-center justify-between gap-3">
                        <span className="text-[10px] text-slate-300 font-medium">
                          {isUploaded ? "Replace or recapture document photo" : "Capture or select document photo from gallery"}
                        </span>

                        <label className={`cursor-pointer inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all shadow-md flex-shrink-0 ${isUploaded
                            ? 'kiosk-apple-btn-secondary'
                            : 'kiosk-apple-btn-primary'
                          }`}>
                          {isBusy ? (
                            <span className="w-3.5 h-3.5 border-2 border-slate-950 border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <>
                              <FaCamera size={11} /> {isUploaded ? "Retake" : "Snap / Upload"}
                            </>
                          )}
                          <input
                            type="file"
                            accept="image/*,application/pdf"
                            capture="environment"
                            onChange={(e) => handleFileUpload(slot.key, e)}
                            className="hidden"
                          />
                        </label>
                      </div>

                      {/* Uploaded Thumbnail & Extracted AI OCR Summary */}
                      {isUploaded && (
                        <div className="space-y-2 pt-1 border-t border-white/10">
                          <div className="flex items-center gap-3 kiosk-apple-card-subtle p-3 rounded-2xl border border-white/10">
                            <img
                              src={doc.previewUrl || doc.s3Url}
                              alt="Doc thumbnail"
                              className="w-14 h-12 object-cover rounded-xl border border-white/20 flex-shrink-0"
                            />
                            <div className="flex-1 min-w-0 text-xs">
                              <div className="flex items-center gap-1.5 text-emerald-400 font-bold text-[11px]">
                                <FaCheckCircle />
                                <span>Uploaded to S3 & AI OCR Validated</span>
                              </div>
                              <span className="text-[9px] text-slate-400 block truncate mt-0.5">
                                {doc.fileName} &middot; {doc.uploadedAt}
                              </span>
                            </div>
                          </div>

                          {/* OCR Extracted Data Cards */}
                          <div className="kiosk-apple-card-subtle p-3.5 rounded-2xl border border-white/10 space-y-2 text-[11px]">
                            <div className="flex justify-between items-center text-[9px] font-black uppercase">
                              <span className="flex items-center gap-1.5 kiosk-text-gradient-cyan"><FaCheckCircle className="text-emerald-400" /> AI OCR EXTRACTED DETAILS</span>
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingOcrSlot(slot.key);
                                  setEditOcrFields({ ...(doc?.aiChecks?.fields || {}) });
                                }}
                                className="kiosk-apple-btn-secondary px-2.5 py-1 rounded-lg text-[9px] font-bold flex items-center gap-1 cursor-pointer transition-colors"
                              >
                                <FaEdit size={9} /> Edit / Review Data
                              </button>
                            </div>

                            <div className="grid grid-cols-2 gap-1.5 text-slate-300 pt-1">
                              {Object.entries(ocrFields).map(([k, v]) => (
                                <div key={k} className="truncate bg-black/40 p-2 rounded-xl border border-white/10">
                                  <span className="text-slate-400 capitalize text-[8px] block font-bold truncate">{k.replace(/([A-Z])/g, ' $1')}</span>
                                  <strong className="text-white text-[10px] block truncate">{String(v)}</strong>
                                </div>
                              ))}
                            </div>

                            {/* Missing info / compliance issues warnings */}
                            {ocrIssues.length > 0 && (
                              <div className="mt-1 pt-1.5 border-t border-white/10 flex items-start gap-1.5 text-amber-300 text-[10px]">
                                <FaExclamationTriangle className="flex-shrink-0 mt-0.5" size={10} />
                                <span>{ocrIssues[0]}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            <div className="pt-2 flex gap-3">
              <button
                type="button"
                onClick={() => setActiveStep(1)}
                className="w-1/3 kiosk-apple-btn-secondary py-3.5 rounded-2xl text-xs font-bold"
              >
                &larr; Back
              </button>
              <button
                type="button"
                onClick={() => setActiveStep(3)}
                className="flex-1 kiosk-apple-btn-primary py-3.5 rounded-2xl text-xs uppercase tracking-wider shadow-lg font-black"
              >
                Sign (Step 3/3) &rarr;
              </button>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* STEP 3: DIGITAL SIGNATURE & SUBMISSION                                    */}
        {/* ========================================================================= */}
        {activeStep === 3 && (
          <div className="space-y-4 animate-fadeIn">
            {!submissionComplete ? (
              <>
                <div className="kiosk-apple-card p-5 space-y-2 shadow-xl border border-white/15">
                  <div className="flex items-center gap-2">
                    <FaSignature size={16} className="text-cyan-400" />
                    <span className="text-[10px] font-black uppercase tracking-wider kiosk-text-gradient-cyan">STEP 3 &middot; DIGITAL SIGNATURE</span>
                  </div>
                  <h3 className="text-lg font-black kiosk-text-gradient-hero">Sign & finalize your application</h3>
                  <p className="text-xs text-slate-300 leading-relaxed">
                    Review your attached documents and sign digitally below to transmit your verified financing packet to your Sales Consultant.
                  </p>
                </div>

                {/* Document Summary Checklist */}
                <div className="kiosk-apple-card p-5 space-y-3 shadow-xl text-xs border border-white/15">
                  <span className="text-[10px] font-black uppercase tracking-widest block border-b border-white/10 pb-2 kiosk-text-gradient-cyan">
                    SUBMITTED DOCUMENTATION CHECKLIST
                  </span>

                  <div className="space-y-2">
                    {["Driver's License (Front)", "Driver's License (Back)", "Proof of Income", "Insurance Card"].map((slot) => (
                      <div key={slot} className="flex justify-between items-center py-1 border-b border-white/5">
                        <span className="text-slate-300">{slot}</span>
                        {uploadedDocs[slot] ? (
                          <span className="text-emerald-400 font-bold flex items-center gap-1 text-[11px]">
                            <FaCheck size={10} /> Attached (S3 &amp; OCR)
                          </span>
                        ) : (
                          <span className="text-amber-400 font-bold text-[11px]">
                            🟡 Pending
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Digital Signature Pad */}
                <div className="kiosk-apple-card p-5 space-y-3 shadow-xl border border-white/15">
                  <div className="flex flex-wrap justify-between items-center gap-2 border-b border-white/10 pb-2.5">
                    <span className="text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 kiosk-text-gradient-cyan">
                      <FaPenNib size={11} /> CUSTOMER DIGITAL SIGNATURE
                    </span>
                    <div className="flex flex-wrap gap-1.5">
                      <button
                        type="button"
                        onClick={() => setSignatureType("DRAW")}
                        className={`px-3 py-1 rounded-xl text-[10px] font-bold transition-all ${signatureType === "DRAW" ? 'kiosk-apple-chip-active' : 'kiosk-apple-chip'
                          }`}
                      >
                        ✍️ Draw Signature
                      </button>
                      <button
                        type="button"
                        onClick={() => setSignatureType("UPLOAD")}
                        className={`px-3 py-1 rounded-xl text-[10px] font-bold transition-all ${signatureType === "UPLOAD" ? 'kiosk-apple-chip-active' : 'kiosk-apple-chip'
                          }`}
                      >
                        📸 Upload Sign Image
                      </button>
                      <button
                        type="button"
                        onClick={() => setSignatureType("TYPE")}
                        className={`px-3 py-1 rounded-xl text-[10px] font-bold transition-all ${signatureType === "TYPE" ? 'kiosk-apple-chip-active' : 'kiosk-apple-chip'
                          }`}
                      >
                        ⌨️ Type Name
                      </button>
                    </div>
                  </div>

                  {/* 1. DRAW SIGNATURE OPTION */}
                  {signatureType === "DRAW" && (
                    <div className="space-y-2">
                      <div className="relative border-2 border-dashed border-cyan-400/40 rounded-2xl bg-black/40 h-36 w-full touch-none overflow-hidden">
                        <canvas
                          ref={signatureCanvasRef}
                          width={400}
                          height={144}
                          onMouseDown={startDrawing}
                          onMouseMove={draw}
                          onMouseUp={stopDrawing}
                          onTouchStart={startDrawing}
                          onTouchMove={draw}
                          onTouchEnd={stopDrawing}
                          className="w-full h-full cursor-crosshair"
                        />
                        {!drawnSignatureData && (
                          <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-slate-400 text-xs font-serif italic">
                            ✍️ Draw your legal signature here with your finger
                          </div>
                        )}
                      </div>
                      <div className="flex justify-between items-center text-[10px]">
                        <span className="text-slate-400 font-medium">Draw legal signature using finger or stylus</span>
                        <button
                          type="button"
                          onClick={clearSignature}
                          className="text-slate-400 hover:text-rose-400 font-bold underline cursor-pointer"
                        >
                          Clear &amp; Redraw
                        </button>
                      </div>
                    </div>
                  )}

                  {/* 2. UPLOAD SIGNATURE IMAGE OPTION */}
                  {signatureType === "UPLOAD" && (
                    <div className="space-y-2">
                      {uploadedSignatureData ? (
                        <div className="space-y-2">
                          <div className="border-2 border-emerald-500/40 bg-white rounded-2xl p-3 h-36 w-full flex items-center justify-center overflow-hidden shadow-inner">
                            <img
                              src={uploadedSignatureData}
                              alt="Uploaded Signature"
                              className="max-h-full max-w-full object-contain"
                            />
                          </div>
                          <div className="flex justify-between items-center text-[10px]">
                            <span className="text-emerald-400 font-bold flex items-center gap-1">
                              <FaCheck size={10} /> Signature Image Attached
                            </span>
                            <div className="flex gap-2">
                              <label className="text-cyan-400 hover:text-cyan-300 font-bold underline cursor-pointer">
                                🔄 Replace Image
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={handleSignatureImageUpload}
                                  className="hidden"
                                />
                              </label>
                              <button
                                type="button"
                                onClick={() => setUploadedSignatureData("")}
                                className="text-rose-400 hover:text-rose-300 font-bold underline cursor-pointer"
                              >
                                ✕ Clear
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <label className="border-2 border-dashed border-cyan-400/40 hover:border-cyan-400 rounded-2xl bg-black/40 p-6 flex flex-col items-center justify-center cursor-pointer transition-all hover:bg-black/60 text-center space-y-2 group">
                          <div className="w-10 h-10 rounded-full bg-cyan-500/15 text-cyan-400 flex items-center justify-center group-hover:scale-110 transition-transform">
                            <FaUpload size={16} />
                          </div>
                          <div className="space-y-0.5">
                            <span className="text-xs font-bold text-white block">Snap or Upload Signature Image</span>
                            <span className="text-[10px] text-slate-400 leading-tight block">
                              Sign on a sheet of paper and take a photo, or choose an image file (PNG, JPG)
                            </span>
                          </div>
                          <span className="text-[9px] kiosk-apple-btn-primary px-3.5 py-1.5 rounded-xl uppercase tracking-wider shadow-md mt-1 font-black">
                            📸 Camera / Browse Image
                          </span>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleSignatureImageUpload}
                            className="hidden"
                          />
                        </label>
                      )}
                    </div>
                  )}

                  {/* 3. TYPE NAME OPTION */}
                  {signatureType === "TYPE" && (
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={typedSignature || `${customerInfo.firstName} ${customerInfo.lastName}`}
                        onChange={(e) => setTypedSignature(e.target.value)}
                        placeholder="Type full legal name"
                        className="w-full bg-black/40 border border-white/15 focus:border-cyan-400 rounded-2xl px-4 py-3.5 text-cyan-300 font-serif italic text-lg outline-none"
                      />
                      <span className="text-[10px] text-slate-400 block">
                        Will be formatted legally under E-SIGN Act standards.
                      </span>
                    </div>
                  )}

                  {/* Credit Authorization Checkbox */}
                  <label className="flex items-start gap-3 cursor-pointer pt-2 border-t border-white/10">
                    <input
                      type="checkbox"
                      checked={creditAuthAgreed}
                      onChange={(e) => setCreditAuthAgreed(e.target.checked)}
                      className="w-5 h-5 rounded-lg text-cyan-400 accent-cyan-400 mt-0.5 cursor-pointer flex-shrink-0"
                    />
                    <div className="space-y-0.5 text-xs">
                      <span className="font-black text-white block">Credit Authorization &amp; Accuracy Certification</span>
                      <p className="text-[10px] text-slate-400 leading-snug">
                        I certify that all uploaded documentation and income information are accurate, and authorize the dealership to perform credit inquiry underwriting.
                      </p>
                    </div>
                  </label>
                </div>

                <div className="pt-2 flex gap-3">
                  <button
                    type="button"
                    onClick={() => setActiveStep(2)}
                    className="w-1/3 kiosk-apple-btn-secondary py-4 rounded-2xl text-xs font-bold"
                  >
                    &larr; Back
                  </button>
                  <button
                    type="button"
                    disabled={isSubmitting}
                    onClick={handleFinalSubmit}
                    className="flex-1 kiosk-apple-btn-primary py-4 rounded-2xl text-xs uppercase tracking-wider shadow-2xl font-black flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <span className="w-4 h-4 border-2 border-slate-950 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <FaPaperPlane size={13} /> Submit Signed Documentation &rarr;
                      </>
                    )}
                  </button>
                </div>
              </>
            ) : (
              /* Success / Transmitted State */
              <div className="kiosk-apple-card p-6 border-2 border-emerald-500/50 text-center space-y-4 shadow-2xl animate-scaleIn">
                <div className="w-16 h-16 rounded-full bg-emerald-500/20 border-2 border-emerald-400 text-emerald-400 flex items-center justify-center mx-auto text-2xl shadow-[0_0_30px_rgba(16,185,129,0.3)]">
                  <FaCheck />
                </div>
                <div className="space-y-1.5">
                  <span className="text-[10px] font-black uppercase tracking-widest block kiosk-text-gradient-emerald">
                    APPLICATION TRANSMITTED LIVE
                  </span>
                  <h3 className="text-xl font-black text-white">Documentation Signed &amp; Submitted!</h3>
                  <p className="text-xs text-slate-300 leading-relaxed max-w-sm mx-auto">
                    Your Sales Consultant has received your complete deal package, S3 documents, OCR extractions, and signature on their desk workstation.
                  </p>
                </div>

                <div className="kiosk-apple-card-subtle p-4 rounded-2xl text-left space-y-2 text-xs border border-white/10">
                  <div className="flex justify-between items-center text-[11px]">
                    <span className="text-slate-400">Deal Status:</span>
                    <span className="text-emerald-400 font-black flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> {deal?.status || "IN_PROGRESS / REVIEW"}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-[11px]">
                    <span className="text-slate-400">Deal Reference:</span>
                    <span className="font-bold text-white">#{deal?.dealNumber || "PHS-00124"}</span>
                  </div>
                  <div className="flex justify-between items-center text-[11px]">
                    <span className="text-slate-400">S3 Documents:</span>
                    <span className="font-bold kiosk-text-gradient-cyan">{Object.keys(uploadedDocs).length} Uploaded &amp; OCR Analyzed</span>
                  </div>
                </div>

                <div className="pt-2 space-y-2">
                  <button
                    type="button"
                    onClick={() => setShowPrintReportModal(true)}
                    className="w-full kiosk-apple-btn-primary py-3.5 rounded-2xl text-xs uppercase tracking-wider shadow-2xl flex items-center justify-center gap-2 font-black"
                  >
                    <FaPrint size={14} /> View &amp; Print Official Buyer's Order (PDF)
                  </button>

                  <button
                    onClick={() => setActiveStep(2)}
                    className="text-xs text-cyan-400 hover:text-cyan-300 font-bold underline cursor-pointer block mx-auto pt-1"
                  >
                    View or add additional documentation
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

      </main>

      {/* Floating Bottom Live Sync Status */}
      <footer className="fixed bottom-0 left-0 right-0 z-40 kiosk-apple-glass-header border-t border-white/10 p-3 max-w-lg mx-auto w-full flex items-center justify-between text-[11px]">
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-ping" />
          <span className="text-slate-300 font-medium">Session: <strong className="text-white">{customerInfo.firstName} {customerInfo.lastName}</strong></span>
        </div>
        <button
          type="button"
          onClick={() => {
            fetchDeal(true);
            toast.info("Refreshed live deal status.");
          }}
          className="flex items-center gap-1.5 kiosk-apple-chip-active px-3 py-1 rounded-xl cursor-pointer"
        >
          <FaSyncAlt size={10} /> Sync Now
        </button>
      </footer>

      {/* EDIT / REVIEW OCR FIELDS MODAL */}
      {editingOcrSlot && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 animate-fadeIn">
          <div className="kiosk-apple-card border-2 border-cyan-500/40 rounded-3xl p-5 max-w-md w-full max-h-[85vh] flex flex-col shadow-2xl space-y-4">
            <div className="flex justify-between items-center border-b border-white/10 pb-3 flex-shrink-0">
              <div>
                <span className="text-[9px] font-black uppercase tracking-widest block kiosk-text-gradient-cyan">AI OCR FIELD INSPECTOR</span>
                <h3 className="text-sm font-black text-white">{editingOcrSlot}</h3>
              </div>
              <button
                onClick={() => setEditingOcrSlot(null)}
                className="text-slate-400 hover:text-white text-xs cursor-pointer p-1"
              >
                ✕
              </button>
            </div>

            <div className="flex-1 overflow-y-auto space-y-3 pr-1 text-xs">
              <p className="text-[11px] text-slate-300">
                Review or correct any extracted details before submitting to the salesperson:
              </p>
              {Object.entries(editOcrFields).map(([key, val]) => (
                <div key={key} className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-400 capitalize block">
                    {key.replace(/([A-Z])/g, ' $1')}
                  </label>
                  <input
                    type="text"
                    value={val}
                    onChange={(e) => setEditOcrFields({ ...editOcrFields, [key]: e.target.value })}
                    className="w-full bg-black/40 border border-white/15 focus:border-cyan-400 rounded-xl px-3 py-2 text-xs text-white outline-none"
                  />
                </div>
              ))}
            </div>

            <div className="flex gap-2.5 pt-2 border-t border-white/10 flex-shrink-0">
              <button
                type="button"
                onClick={() => setEditingOcrSlot(null)}
                className="w-1/3 kiosk-apple-btn-secondary py-2.5 rounded-xl text-xs font-bold"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleSaveOcrFields(editingOcrSlot, editOcrFields)}
                className="flex-1 kiosk-apple-btn-primary py-2.5 rounded-xl text-xs uppercase tracking-wider shadow-lg font-black"
              >
                Save &amp; Verify &rarr;
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Official Automotive Buyer's Order / Deal Sheet Modal */}
      <DealPrintReport
        isOpen={showPrintReportModal}
        onClose={() => setShowPrintReportModal(false)}
        deal={deal}
        activeCar={deal?.vehicle || {}}
        customerInfo={customerInfo}
        uploadedDocs={uploadedDocs}
        salespersonId={deal?.salespersonName || deal?.salesperson?.name || "Bobby Bonser (Sales Consultant)"}
        dealershipData={deal?.dealership || {}}
        paymentDetails={{
          downPayment: customerInfo.downPayment || deal?.finance?.downPayment || 0,
          customerRebate: deal?.finance?.rebate || 0,
          tradeEquity: deal?.trade?.equity || 0,
          apr: deal?.finance?.apr || 5.99,
          term: deal?.finance?.term || 72,
          paymentPreference: deal?.finance?.paymentType || "FINANCE"
        }}
      />
    </div>
  );
};

export default CustomerMobileKiosk;
